import { Button } from "@/components/ui/button";
import { LayoutDashboard, Menu } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleDashboardClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/auth');
        return;
      }

      // Check user role
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking user role:', error);
        toast({
          title: "Error",
          description: "Could not determine user role",
          variant: "destructive",
        });
        return;
      }

      // Navigate based on role
      if (roles?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/developer-dashboard');
      }
    } catch (error) {
      console.error('Navigation error:', error);
      toast({
        title: "Error",
        description: "Could not access dashboard",
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="fixed w-full bg-white/80 backdrop-blur-md z-50 border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="text-xl font-bold text-primary">
              Triple Digit Talent
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 ml-8">
           <Link to="/developers" className="text-gray-600 hover:text-primary">
              For Developers
            </Link>
            <div className="md:flex items-center space-x-4 ml-8">
              <Link to="/hire-developers">
                <Button variant="default" className="bg-primary hover:bg-primary/90">
                  For companies
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={handleDashboardClick}
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="pt-2 pb-3 space-y-1">
              <Link
                to="/developers"
                className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-primary hover:bg-gray-50"
              >
                For Developers
              </Link>
              <Link to="/hire-developers">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  Hire Developers
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleDashboardClick}
              >
                Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};